import * as DialogPrimitive from '@radix-ui/react-dialog'
import { cn } from '@/lib/utils'
import { X } from 'lucide-react'
import type { ReactNode } from 'react'

/**
 * IntuiDrawer — Caliper Design System
 *
 * Figma specs:
 * - Directions: top, bottom, left, right
 * - Overlay: bg-white/90 dark:bg-black/80 with backdrop-blur-sm
 * - Wrapper: bg-background, border-border, rounded-xl (14px) on slide-in edge
 * - Handle bar (bottom/top): 120px wide, h-2, bg-muted, rounded-full
 * - Header: p-4, title text-base font-medium, description text-sm text-muted-foreground
 * - Content: px-4 pt-4
 * - Footer: p-4 flex flex-col gap-2
 */

export type DrawerDirection = 'top' | 'bottom' | 'left' | 'right'
export type DrawerSize = 'sm' | 'default' | 'lg' | 'full'

const sizeClasses: Record<DrawerDirection, Record<DrawerSize, string>> = {
  bottom: { sm: 'max-h-[30vh]', default: 'max-h-[50vh]', lg: 'max-h-[75vh]', full: 'max-h-[90vh]' },
  top: { sm: 'max-h-[30vh]', default: 'max-h-[50vh]', lg: 'max-h-[75vh]', full: 'max-h-[90vh]' },
  left: { sm: 'max-w-sm', default: 'max-w-md', lg: 'max-w-xl', full: 'max-w-2xl' },
  right: { sm: 'max-w-sm', default: 'max-w-md', lg: 'max-w-xl', full: 'max-w-2xl' },
}

const directionClasses: Record<DrawerDirection, string> = {
  bottom: 'inset-x-0 bottom-0 rounded-t-xl border-t',
  top: 'inset-x-0 top-0 rounded-b-xl border-b',
  left: 'inset-y-0 left-0 rounded-r-xl border-r',
  right: 'inset-y-0 right-0 rounded-l-xl border-r-0 border-l',
}

const slideAnimations: Record<DrawerDirection, string> = {
  bottom:
    'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
  top:
    'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top',
  left:
    'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left',
  right:
    'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
}

export interface IntuiDrawerProps {
  /** Drawer title */
  title?: string
  /** Drawer description */
  description?: string
  /** Main content */
  children: ReactNode
  /** Footer content (buttons) */
  footer?: ReactNode
  /** Trigger element */
  trigger?: ReactNode
  /** Controlled open state */
  open?: boolean
  /** Open state change callback */
  onOpenChange?: (open: boolean) => void
  /** Slide direction */
  direction?: DrawerDirection
  /** Drawer size */
  size?: DrawerSize
  /** Show the drag handle bar (for top/bottom) */
  showHandle?: boolean
  /** Show close button */
  showClose?: boolean
  /** Additional className for content */
  className?: string
}

function IntuiDrawer({
  title,
  description,
  children,
  footer,
  trigger,
  open,
  onOpenChange,
  direction = 'bottom',
  size = 'default',
  showHandle = true,
  showClose = false,
  className,
}: IntuiDrawerProps) {
  const isVertical = direction === 'top' || direction === 'bottom'

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      {trigger && (
        <DialogPrimitive.Trigger asChild>{trigger}</DialogPrimitive.Trigger>
      )}
      <DialogPrimitive.Portal>
        {/* Figma: overlay with blur */}
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-[999999] bg-white/90 dark:bg-black/80 backdrop-blur-sm',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0'
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            'fixed z-[999999] flex flex-col bg-background border-border border-solid',
            'duration-300 ease-in-out',
            directionClasses[direction],
            slideAnimations[direction],
            isVertical ? 'w-full' : 'h-full',
            sizeClasses[direction][size],
            className
          )}
        >
          {/* Handle bar — Figma: 120px w, 8px h, bg-muted, rounded-full, pt-4 centered */}
          {showHandle && isVertical && direction === 'bottom' && (
            <div className="flex flex-col items-center pt-4">
              <div className="h-2 w-[120px] rounded-full bg-muted" />
            </div>
          )}

          {/* Header — Figma: p-4, text-base font-medium title, text-sm muted description */}
          {(title || description) && (
            <div className="flex flex-col gap-0.5 p-4 text-center">
              {title && (
                <DialogPrimitive.Title className="text-base font-medium text-foreground">
                  {title}
                </DialogPrimitive.Title>
              )}
              {description && (
                <DialogPrimitive.Description className="text-sm text-muted-foreground">
                  {description}
                </DialogPrimitive.Description>
              )}
            </div>
          )}

          {/* Content — Figma: px-4 pt-4 overflow */}
          <div className="flex-1 overflow-auto px-4 pt-4">{children}</div>

          {/* Footer — Figma: p-4 flex flex-col gap-2 */}
          {footer && (
            <div className="flex flex-col gap-2 p-4">{footer}</div>
          )}

          {/* Handle bar for top direction */}
          {showHandle && isVertical && direction === 'top' && (
            <div className="flex flex-col items-center pb-4">
              <div className="h-2 w-[120px] rounded-full bg-muted" />
            </div>
          )}

          {/* Close button */}
          {showClose && (
            <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

export { IntuiDrawer }
